import React from 'react';
import { ActionMenuList, ListItem, Tooltip } from '@ds';
import {
  DSCopyIcon,
  DSDefaultViewIcon,
  DSDeleteIcon,
  DSEditIcon
} from '@ds-icons';
import styles from './ViewSelect.module.scss';
import { ViewItemDisabledActions } from './utils';

type ViewSelectActionsProps = {
  isDefault?: boolean;
  onEditView?: () => void;
  onDuplicateView: () => void;
  onSetDefaultView?: () => void;
  onDeleteView?: () => void;
  editDisabledTooltip?: string;
  deleteDisabledTooltip?: string;
  disabled?: boolean;
  disabledActions?: ViewItemDisabledActions;
  duplicateDisableTooltip?: string;
};
const ViewSelectActions: React.FC<ViewSelectActionsProps> = ({
  isDefault = false,
  onDeleteView,
  onDuplicateView,
  onEditView,
  onSetDefaultView,
  editDisabledTooltip,
  deleteDisabledTooltip,
  disabled,
  disabledActions = {},
  duplicateDisableTooltip
}) => {
  const editActionEnabled = Boolean(onEditView) && !disabledActions['edit'];
  const deleteActionEnabled =
    Boolean(onDeleteView) && !disabledActions['delete'];
  const setDefaultActionEnabled =
    Boolean(onSetDefaultView) && !disabledActions['default'];
  const duplicateActionEnabled =
    Boolean(onDuplicateView) && !disabledActions['duplicate'];

  return (
    <ListItem.ActionContainer>
      {isDefault && (
        <ListItem.Action
          className={styles.optionDefaultIcon}
          Icon={<DSDefaultViewIcon />}
          data-test="view-select-default-icon"
        />
      )}
      {!disabled && (
        <ActionMenuList className={styles.optionSettingsWrapper}>
          <ActionMenuList.Trigger
            size="small"
            data-test="view-select-action-list-trigger"
          />
          <ActionMenuList.Body
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            paperClassName={styles.optionSettingsContainer}
          >
            <Tooltip
              disableListeners={editActionEnabled}
              content={editDisabledTooltip}
            >
              <ListItem
                dataTest="view-select-action-list-edit"
                onClick={onEditView}
                disabled={!editActionEnabled}
              >
                <ListItem.PrefixContainer>
                  <ListItem.Icon Icon={<DSEditIcon />} />
                </ListItem.PrefixContainer>
                <ListItem.Text primary="Edit" />
              </ListItem>
            </Tooltip>

            <Tooltip
              disableListeners={duplicateActionEnabled}
              content={duplicateDisableTooltip}
            >
              <ListItem
                dataTest="view-select-action-list-duplicate"
                onClick={onDuplicateView}
                disabled={!duplicateActionEnabled}
              >
                <ListItem.PrefixContainer>
                  <ListItem.Icon Icon={<DSCopyIcon />} />
                </ListItem.PrefixContainer>
                <ListItem.Text primary="Duplicate" />
              </ListItem>
            </Tooltip>

            <ListItem
              dataTest="view-select-action-list-set-default"
              disabled={!setDefaultActionEnabled || isDefault}
              onClick={onSetDefaultView}
            >
              <ListItem.PrefixContainer>
                <ListItem.Icon Icon={<DSDefaultViewIcon />} />
              </ListItem.PrefixContainer>
              <ListItem.Text primary="Set as default" />
            </ListItem>

            <Tooltip
              disableListeners={deleteActionEnabled}
              content={deleteDisabledTooltip}
            >
              <ListItem
                dataTest="view-select-action-list-delete"
                onClick={onDeleteView}
                disabled={!deleteActionEnabled}
              >
                <ListItem.PrefixContainer>
                  <ListItem.Icon Icon={<DSDeleteIcon />} />
                </ListItem.PrefixContainer>
                <ListItem.Text primary="Delete" />
              </ListItem>
            </Tooltip>
          </ActionMenuList.Body>
        </ActionMenuList>
      )}
    </ListItem.ActionContainer>
  );
};

export default ViewSelectActions;
